import { Inject, Injectable } from '@angular/core';
import { ConfigInstanceToken, ConfigInterface } from '../../interfaces/config.interface';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import * as _ from 'lodash';
import moment from 'moment';
import { Observable } from 'rxjs';
import { TimeRecordingEntryModel } from '../../models/complex/time-recording-entry.model';
import { TimesheetEntryInterface } from '../../models/complex/timesheet-entry.interface';
import { TimesheetEntryModel } from '../../models/complex/timesheet-entry.model';
import { TimesheetGapModel } from '../../models/complex/timesheet-gap.model';
import { NotificationIdentifierModel } from '../../models/low-level/notification-identifier.model';
import { TimesheetModel } from '../../models/low-level/timesheet.model';
import { DateModel } from '../../models/primitive/date.model';
import { TimeModel } from '../../models/primitive/time.model';
import { TimesheetAndExpensesInstanceToken, TimesheetAndExpensesInterface } from './timesheet-and-expenses.interface';
import {
    isEowEntry,
    isGapEntry,
    isManualEntry,
    isNormalEntry,
    isSowEntry,
    isTravelBackEntry,
    isTravelToEntry,
} from '../../utilities/timesheet-entry.utils';
import { map } from 'rxjs/operators';
import { UserMasterInstanceToken, UserMasterInterface } from '../../interfaces/user-master.interface';

export class TimesheetNormalEditableEntriesValidationResult {
    public constructor(
        public isInvalid = false,
        public notificationId = new NotificationIdentifierModel('', '', ''), // notification for which the entry is invalid
        public validationErrorMessage = '',
    ) {}
}

@Injectable({
    providedIn: 'root',
})
export class TimesheetService {
    public isAutoSaveAllowedForNextChange: boolean;
    public constructor(
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        @Inject(TimesheetAndExpensesInstanceToken)
        private readonly timesheetAndExpensesService: TimesheetAndExpensesInterface,
        @Inject(UserMasterInstanceToken) private readonly userMasterService: UserMasterInterface,
        @Inject(ConfigInstanceToken) private readonly configService: ConfigInterface,
    ) {
        this.isAutoSaveAllowedForNextChange = true;
    }

    /**
     * Sort the entries in the timesheet according to their start times
     *
     * @param entries All the entries from the timesheet
     * @returns The sorted list of entries
     * @throws Error if client is not recognized
     */
    public sortEntries(entries: TimesheetEntryInterface[]): TimesheetEntryInterface[] {
        // store SoW and EoW if they are already set
        const sow = _.first(entries);
        if (sow && isSowEntry(sow)) {
            entries.shift();
        }
        const eow = _.last(entries);
        if (eow && isEowEntry(eow)) {
            entries.pop();
        }
        // sort only the notification and manual entries, as the gap entries are added via closeGaps method.
        entries = entries.filter((entry) => !isGapEntry(entry));
        const sortedEntries = TimesheetModel.sortTimesheetEntries(entries);

        if (sow && isSowEntry(sow)) {
            sortedEntries.unshift(sow);
        }
        if (eow && isEowEntry(eow)) {
            sortedEntries.push(eow);
        }

        return sortedEntries;
    }

    public closeGaps(entries: TimesheetEntryInterface[]): TimesheetEntryInterface[] {
        const fixedList: TimesheetEntryInterface[] = [];
        let previousEntry: TimesheetEntryInterface | undefined;

        const minimumNumberOfEntries = 2;

        // gaps can only happen between at least two entries
        if (entries.length < minimumNumberOfEntries) {
            return entries;
        }

        entries
            .filter((entry) => !isGapEntry(entry))
            .forEach((entry) => {
                // check if there is a gap
                if (previousEntry && entry.startTime.value.diff(previousEntry.endTime.value) > 0) {
                    fixedList.push(
                        new TimesheetGapModel(previousEntry.endTime.value.clone(), entry.startTime.value.clone()),
                    );
                }

                // add entry to fixed list
                fixedList.push(entry);

                // update previous entry
                previousEntry = entry;
            });

        this.updateStartAndEndLimits(fixedList);

        return fixedList;
    }

    /**
     * Takes a gap-less list of entries and updates the minValue for the start time and the max value for the end time
     * so that no overlap is allowed to be entered by the user
     *
     * @param {TimesheetEntryInterface[]} entries the list of timesheet entries
     */
    private updateStartAndEndLimits(entries: TimesheetEntryInterface[]): void {
        let previousEntry: TimesheetEntryInterface | undefined;
        const tomorrowEndOfDay = moment().add(1, 'day').endOf('day');

        entries
            .filter((entry) => !isGapEntry(entry))
            .forEach((entry) => {
                if (previousEntry) {
                    const maxValueTime = entry.startTime.value.isAfter(tomorrowEndOfDay)
                        ? tomorrowEndOfDay.toObject()
                        : entry.startTime.value.toObject();
                    const minValueTime = previousEntry.endTime.value.toObject();

                    // Due to the fact that the date and time are binded to other components,
                    // we need to change the reference for the changeDetection to work for all the date time inputs
                    const previousEntryStartDateTime = previousEntry.startDate.value;
                    let previousEntryEndDateTime = previousEntry.endDate.value;
                    if (isSowEntry(previousEntry)) {
                        previousEntryEndDateTime = previousEntryStartDateTime;
                    }

                    const previousEntryStartDateOptions = previousEntry.startDate.options;
                    previousEntryStartDateOptions.maxValue.set(maxValueTime);
                    previousEntry.startDate = <DateModel>(
                        previousEntry.set(
                            new DateModel(
                                previousEntry.startDate.name,
                                previousEntryStartDateTime,
                                previousEntryStartDateOptions,
                            ),
                        )
                    );
                    const previousEntryStartTimeOptions = previousEntry.startTime.options;
                    previousEntryStartTimeOptions.maxValue.set(maxValueTime);
                    previousEntry.startTime = <TimeModel>(
                        previousEntry.set(
                            new TimeModel(
                                previousEntry.startTime.name,
                                previousEntryStartDateTime,
                                previousEntryStartTimeOptions,
                            ),
                        )
                    );
                    const previousEntryEndDateOptions = previousEntry.endDate.options;
                    previousEntryEndDateOptions.maxValue.set(maxValueTime);
                    previousEntry.endDate = <DateModel>(
                        previousEntry.set(
                            new DateModel(
                                previousEntry.endDate.name,
                                previousEntryEndDateTime,
                                previousEntryEndDateOptions,
                            ),
                        )
                    );
                    const previousEntryEndTimeOptions = previousEntry.endTime.options;
                    previousEntryEndTimeOptions.maxValue.set(maxValueTime);
                    previousEntry.endTime = <TimeModel>(
                        previousEntry.set(
                            new TimeModel(
                                previousEntry.endTime.name,
                                previousEntryEndDateTime,
                                previousEntryEndTimeOptions,
                            ),
                        )
                    );

                    let entryStartDateTime = entry.startDate.value;
                    const entryEndDateTime = entry.endDate.value;

                    const entryEndDateOptions = entry.endDate.options;
                    entryEndDateOptions.minValue.set(maxValueTime);
                    entry.endDate = <DateModel>(
                        entry.set(new DateModel(entry.endDate.name, entryEndDateTime, entryEndDateOptions))
                    );
                    const entryEndTimeOptions = entry.endTime.options;
                    entryEndTimeOptions.minValue.set(maxValueTime);
                    entry.endTime = <TimeModel>(
                        entry.set(new TimeModel(entry.endTime.name, entryEndDateTime, entryEndTimeOptions))
                    );

                    if (isEowEntry(entry)) {
                        entryStartDateTime = entryEndDateTime;
                        // For EOW entry, the start and end date/time values are same. So, set the minimum values for both to the previous entry's end date/time values.
                        entryEndDateOptions.minValue.set(minValueTime);
                        entry.endDate = <DateModel>(
                            entry.set(new DateModel(entry.endDate.name, entryEndDateTime, entryEndDateOptions))
                        );
                        entryEndTimeOptions.minValue.set(minValueTime);
                        entry.endTime = <TimeModel>(
                            entry.set(new TimeModel(entry.endTime.name, entryEndDateTime, entryEndTimeOptions))
                        );
                    }

                    const entryStartDateOptions = entry.startDate.options;
                    entryStartDateOptions.minValue.set(minValueTime);
                    // reset the max value. start date <= end date
                    entryStartDateOptions.maxValue.set(entry.endTime.value.toObject());
                    entry.startDate = <DateModel>(
                        entry.set(new DateModel(entry.startDate.name, entryStartDateTime, entryStartDateOptions))
                    );
                    const entryStartTimeOptions = entry.startTime.options;
                    entryStartTimeOptions.minValue.set(minValueTime);
                    // reset the max value. start time <= end time
                    entryStartDateOptions.maxValue.set(entry.endTime.value.toObject());
                    entry.startTime = <TimeModel>(
                        entry.set(new TimeModel(entry.startTime.name, entryStartDateTime, entryStartTimeOptions))
                    );
                }

                // update previous entry
                previousEntry = entry;
            });
    }

    /**
     * Check if there are any invalid manual entries.
     * - Every manual entry needs to have an address set and
     * that address should be present in the local storage
     *
     * @param entries the list of timesheet entries
     * @returns true if all manual entries are valid
     */
    public checkManualEntryAddresses(entries: TimesheetEntryInterface[]): boolean {
        const addresses = this.timesheetAndExpensesService.getAddresses();

        entries.map((entry) => {
            if (isManualEntry(entry)) {
                const entryAddress = entry.address;

                if (entryAddress && entryAddress.isValid()) {
                    const existingAddress = addresses.find((addr) => {
                        return (
                            addr.addrText.value === entryAddress.addrText.value &&
                            addr.city.value === entryAddress.city.value &&
                            addr.street.value === entryAddress.street.value &&
                            addr.postCode.value === entryAddress.postCode.value &&
                            addr.addrNumber.value === entryAddress.addrNumber.value &&
                            addr.statesOfUSA?.selectedEntry?.model.value ===
                                entryAddress.statesOfUSA?.selectedEntry?.model.value
                        );
                    });
                    // It is possible that user has just now deleted manually added addresses,
                    // If the manual entry still has such address, it needs to be removed
                    if (!existingAddress) {
                        entry.address = undefined;
                    }
                }
            }
        });

        const invalidManualEntry = entries.find((entry: TimesheetEntryInterface) => {
            let hasInvalidAddress = false;
            if (isManualEntry(entry)) {
                hasInvalidAddress = !(entry.address && entry.address.isValid());
            }
            return hasInvalidAddress;
        });

        // if there is an invalid entry, we want to return false
        return typeof invalidManualEntry === 'undefined';
    }

    /**
     * Check if there are any unfilled mandatory gap entries left
     *
     * @param entries the list of timesheet entries
     * @returns true if there are no unfilled mandatory gap entries
     */
    public checkForUnfilledGaps(entries: TimesheetEntryInterface[]): boolean {
        return entries.filter((entry) => isGapEntry(entry) && entry.mandatory).length === 0;
    }

    public isTimesheetInvalidForNormalEditableEntries(
        timeRecordingEntries: TimeRecordingEntryModel[],
    ): Observable<TimesheetNormalEditableEntriesValidationResult> {
        return this.timesheetAndExpensesService.selectCurrentTimesheet$({ once: true }).pipe(
            map((timesheet: Readonly<TimesheetModel>) => {
                let result = new TimesheetNormalEditableEntriesValidationResult();

                // get first travel to entry of timesheet which is editable
                const firstEditableTravelToEntry = timesheet
                    .getTimesheetEntries()
                    .find((entry) => isNormalEntry(entry) && isTravelToEntry(entry) && entry.isTravelToEditable);
                // check if the current time recording is before the firstEditableTravelToEntry
                if (firstEditableTravelToEntry) {
                    const firstEntryOfTimeRecording = timeRecordingEntries[0];
                    if (
                        firstEntryOfTimeRecording.endDateTime.isSameOrBefore(
                            firstEditableTravelToEntry.endDate.value,
                        ) &&
                        firstEditableTravelToEntry.startDate.value.isAfter(firstEditableTravelToEntry.endDate.value)
                    ) {
                        result = new TimesheetNormalEditableEntriesValidationResult(
                            true,
                            (<TimesheetEntryModel>firstEditableTravelToEntry).notificationId,
                            `Timesheet's first travelTo entry start date is invalid`,
                        );
                    }
                }
                // get last travel back entry of timesheet which is editable
                const lastEditableTravelBackEntry = timesheet
                    .getTimesheetEntries()
                    .find((entry) => isNormalEntry(entry) && isTravelBackEntry(entry) && entry.isTravelBackEditable);
                // check if the current time recording is after the lastEditableTravelBackEntry
                if (lastEditableTravelBackEntry) {
                    const lastEntryOfTimeRecording = timeRecordingEntries[timeRecordingEntries.length - 1];
                    if (
                        lastEntryOfTimeRecording.startDateTime.isSameOrAfter(
                            lastEditableTravelBackEntry.startDate.value,
                        ) &&
                        lastEditableTravelBackEntry.endDate.value.isBefore(lastEditableTravelBackEntry.startDate.value)
                    ) {
                        result = new TimesheetNormalEditableEntriesValidationResult(
                            true,
                            (<TimesheetEntryModel>lastEditableTravelBackEntry).notificationId,
                            `Timesheet's last travelBack entry end date is invalid`,
                        );
                    }
                }
                this.log.debug(
                    `TimesheetService: isTimesheetInvalidForNormalEditableEntries: ${result.isInvalid} ${
                        result.isInvalid
                            ? `for notification ${result.notificationId} ${result.validationErrorMessage}`
                            : ''
                    }`,
                );

                return result;
            }),
        );
    }

    public isTimesheetFeatureEnabled(): boolean {
        return !this.configService.isTimesheetDisabled() && !this.hideTimeSheetBasedOnPersonnelSubArea();
    }

    public hideTimeSheetBasedOnPersonnelSubArea(): boolean {
        const user = this.userMasterService.getSelectedUser();
        const personnelSubArea = user?.personnelSubArea?.trim();
        if (!personnelSubArea) {
            return false;
        }
        const subAreasToHideTimesheet = this.configService
            .getHideTimesheetPersonnelSubArea()
            .split(';')
            .map((h) => h.trim().toLowerCase())
            .filter(Boolean); // filter out empty values

        return subAreasToHideTimesheet.includes(personnelSubArea.toLowerCase());
    }
}
